import * as THREE from 'three';

/** Shared sign text drawing for CharacterEmbed2 (single-scene pipeline). */

export const SPRITE_SHIFT_ALONG_PANEL_DOWN = -0.38;
export const PANEL_TEXT_INSET = 0.8;
export const TEXT_PLANE_SURFACE_OFFSET = 0.04;

/** Toggle: `true` = centered on canvas; `false` = left-aligned (x = padLeft). */
const SIGN_TEXT_CENTERED = true;

export function sanitizeThoughtText(value: string): string {
  return value.replace(/\*\*/g, '').replace(/\s+/g, ' ').trim();
}

export function createSignTextCanvasTexture(): {
  canvas: HTMLCanvasElement;
  texture: THREE.CanvasTexture;
} {
  const canvas = document.createElement('canvas');
  // Fixed 1024×512 (2:1) — matches the visible LED face UV ratio so glyphs render 1:1 without stretch.
  canvas.width = 1024;
  canvas.height = 512;
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;
  return { canvas, texture };
}

export function updateSignTextTexture(
  canvas: HTMLCanvasElement,
  texture: THREE.CanvasTexture,
  nextText: string
): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const width = canvas.width;
  const height = canvas.height;
  ctx.clearRect(0, 0, width, height);

  const normalized = sanitizeThoughtText(nextText);
  if (!normalized) {
    texture.needsUpdate = true;
    return;
  }

  // Horizontal band for wrapping (same whether centered or left).
  // const padLeft = width * 0.16;
  // const padRight = width * 0.10;
  const padLeft = width * (SIGN_TEXT_CENTERED ? 0.12 : 0.16) as number;
  const padRight = width * (SIGN_TEXT_CENTERED ? 0.12 : 0.10) as number;
  const maxLineWidth = width - padLeft - padRight;
  let fontSize = Math.round(height * 0.11);
  const lineHeightFactor = 1.12;
  let lines: string[] = [];

  // No Arial fallback — avoids check() silently matching and painting with the wrong font.
  const canvasFont = () => `800 ${fontSize}px "Roboto"`;

  const buildLines = () => {
    ctx.font = canvasFont();
    const words = normalized.split(' ');
    const built: string[] = [];
    let current = '';
    for (const word of words) {
      const candidate = current ? `${current} ${word}` : word;
      if (ctx.measureText(candidate).width <= maxLineWidth || !current) {
        current = candidate;
      } else {
        built.push(current);
        current = word;
      }
    }
    if (current) built.push(current);
    return built.slice(0, 5);
  };

  lines = buildLines();
  const lh = () => Math.round(fontSize * lineHeightFactor);
  while (
    (lines.length * lh() > height * 0.86 || lines.some((l) => ctx.measureText(l).width > maxLineWidth)) &&
    fontSize > 20
  ) {
    fontSize -= 2;
    lines = buildLines();
  }

  // Check Roboto specifically (no fallbacks) — check() with "Arial, sans-serif" always returns true,
  // so we'd never actually load Roboto and canvas would silently render with Arial instead.
  const robotoCheckStr = canvasFont();
  if ('fonts' in document && !document.fonts.check(robotoCheckStr)) {
    document.fonts.load(robotoCheckStr).then(() => updateSignTextTexture(canvas, texture, nextText)).catch(() => undefined);
    return; // avoid painting with fallback — wait for real Roboto, then redraw
  }

  ctx.textAlign = SIGN_TEXT_CENTERED ? 'center' : 'left';
  ctx.textBaseline = 'middle';
  ctx.font = canvasFont();
  // ctx.font = `italic bold ${fontSize}px system-ui, Arial, sans-serif`;
  // ctx.font = `italic bold ${fontSize}px "Sarabun", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji"`;
  // Thin stroke compensates for bloom (UnrealBloomPass) eroding dark letterforms on bright yellow.
  // lineWidth 0.04 is small enough not to compress counter spaces — it only adds weight to the stems.
  ctx.fillStyle = '#333333';
  // ctx.strokeStyle = '#333333';
  ctx.lineJoin = 'round';
  // ctx.lineWidth = fontSize * 0.04;

  const lineH = lh();
  const totalH = lines.length * lineH;
  const startY = (height - totalH) / 2 + lineH / 2;
  const textX = SIGN_TEXT_CENTERED ? width / 2 : padLeft;
  lines.forEach((line, idx) => {
    const y = startY + idx * lineH;
    // center: x = width/2; left: x = padLeft (left edge of line).
    ctx.fillText(line, textX, y);
  });

  texture.needsUpdate = true;
}

/**
 * Placeholder export so the module stays a named "CharacterSign2" artifact.
 * Sign mesh + text plane live in CharacterEmbed2's scene.
 * When to show thought text (after Sign Anim in → Sign Idle) is set in CharacterEmbed2.applySignVisibility.
 */
export default function CharacterSign2() {
  return null;
}
